import { useAtomValue, useSetAtom } from 'jotai';
import { Button, IconButton } from '@carbon/react';
import { Close, Copy } from '@carbon/react/icons';
import { appStore } from 'components/map';
import { useEscapeToClose } from 'lib/hooks/useEscapeToClose';
import {
  clearPinsAtom,
  closeCoordinatesAtom,
  coordinatePinsAtom,
} from './atoms';
import { CopyToast } from './copy-feedback';
import { copyAllCoordinates, useCopied } from './hooks';
import { LocationField } from './location-field';
import { PinTable } from './pin-table';
import styles from './coordinate-pins.module.scss';

/**
 * The Coordinates card beside the control stack.
 *
 * The Location field, then every dropped pin with its colour, zoom-to, copy and
 * remove.
 *
 * @return {JSX.Element} The card.
 */
export function CoordinatesCard(): JSX.Element {
  const pins = useAtomValue(coordinatePinsAtom, appStore);
  const close = useSetAtom(closeCoordinatesAtom, appStore);
  const clearPins = useSetAtom(clearPinsAtom, appStore);
  const copyAll = useCopied();
  useEscapeToClose(true, close);

  return (
    <div className={styles.card} role="dialog" aria-label="Coordinates">
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>Coordinates</span>
        <IconButton
          kind="ghost"
          size="sm"
          label="Close"
          align="left"
          onClick={() => close()}
        >
          <Close />
        </IconButton>
      </div>
      <LocationField />

      {pins.length > 0 && (
        <div className={styles.pinList}>
          <div className={styles.pinListHead}>
            <span className={styles.pinCount}>
              {pins.length === 1 ? '1 pin' : `${pins.length} pins`}
            </span>
            <div className={styles.pinListActions}>
              <IconButton
                label={copyAll.copied ? 'Copied' : 'Copy all coordinates'}
                kind="ghost"
                size="sm"
                align="left"
                onClick={() =>
                  void copyAll.copy(() =>
                    copyAllCoordinates(pins.map((pin) => pin.lngLat))
                  )
                }
              >
                <Copy />
              </IconButton>
              <CopyToast
                result={copyAll.result}
                onClose={copyAll.clearResult}
              />
              <Button
                kind="danger--ghost"
                size="sm"
                onClick={() => clearPins()}
              >
                Clear all
              </Button>
            </div>
          </div>
          <PinTable pins={pins} />
        </div>
      )}
    </div>
  );
}
